import React, {useState, useEffect, useCallback} from 'react';
import {Button, Chip, CircularProgress, Typography} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {ServiceLocator} from "../../../Services/ServiceLocator";

/**
 * Assigns SEMA/VCdb vehicles (Year -> Make -> Model -> BaseVehicleID) to a product.
 * Stored fitments are loaded/saved via the vehicle service. SEMA lookups may be unavailable
 * (no subscription configured); in that case existing fitments still display.
 */
function VehicleFitmentPanel({productType, productId}) {
    const vehicleService = ServiceLocator.Instance.getVehicleService();

    const [fitments, setFitments] = useState([]);
    const [years, setYears] = useState([]);
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);

    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMake, setSelectedMake] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);

    const [loadingFitments, setLoadingFitments] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [semaUnavailable, setSemaUnavailable] = useState(false);

    const hasProduct = !!productId;

    const loadFitments = useCallback(() => {
        if (!hasProduct) return;
        setLoadingFitments(true);
        vehicleService.getFitments(productType, productId)
            .then((list) => setFitments(Array.isArray(list) ? list : []))
            .catch(() => setFitments([]))
            .finally(() => setLoadingFitments(false));
    }, [hasProduct, productType, productId, vehicleService]);

    useEffect(() => {
        loadFitments();
    }, [loadFitments]);

    // Load years once (SEMA-backed).
    useEffect(() => {
        if (!hasProduct) return;
        vehicleService.getYears()
            .then((list) => {
                setYears(Array.isArray(list) ? list : []);
                setSemaUnavailable(false);
            })
            .catch(() => setSemaUnavailable(true));
    }, [hasProduct, vehicleService]);

    // When year changes, load makes for that year and reset downstream.
    useEffect(() => {
        setSelectedMake(null);
        setSelectedModel(null);
        setModels([]);
        if (!selectedYear) {
            setMakes([]);
            return;
        }
        vehicleService.getMakes(selectedYear)
            .then((list) => setMakes((list || []).map((m) => m.makeName)))
            .catch(() => setSemaUnavailable(true));
    }, [selectedYear, vehicleService]);

    // When make changes, load models for year+make.
    useEffect(() => {
        setSelectedModel(null);
        if (!selectedYear || !selectedMake) {
            setModels([]);
            return;
        }
        setLoadingModels(true);
        vehicleService.getModels(selectedYear, selectedMake)
            .then((list) => setModels(Array.isArray(list) ? list : []))
            .catch(() => setSemaUnavailable(true))
            .finally(() => setLoadingModels(false));
    }, [selectedYear, selectedMake, vehicleService]);

    const handleAdd = () => {
        if (!selectedYear || !selectedMake || !selectedModel) return;
        setSaving(true);
        setError('');
        vehicleService.addFitments(productType, productId, [{
            baseVehicleId: selectedModel.baseVehicleId,
            year: selectedYear,
            makeName: selectedMake,
            modelName: selectedModel.modelName,
        }])
            .then((list) => {
                setFitments(Array.isArray(list) ? list : []);
                setSelectedModel(null);
            })
            .catch((err) => setError((err && err.message) || 'Failed to add vehicle.'))
            .finally(() => setSaving(false));
    };

    const handleRemove = (id) => {
        setError('');
        vehicleService.removeFitment(id)
            .then(() => setFitments((prev) => prev.filter((f) => f.id !== id)))
            .catch((err) => setError((err && err.message) || 'Failed to remove vehicle.'));
    };

    if (!hasProduct) {
        return (
            <div className="vehicle-fitment-panel">
                <Typography variant="subtitle1" style={{fontWeight: 600}}>Vehicle Fitment</Typography>
                <Typography variant="body2" style={{color: '#888'}}>
                    Save this product first to assign vehicles.
                </Typography>
            </div>
        );
    }

    return (
        <div className="vehicle-fitment-panel" style={{marginTop: 16}}>
            <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 4}}>Vehicle Fitment</Typography>
            <Typography variant="body2" style={{color: '#888', marginBottom: 12}}>
                Assign the vehicles (Year / Make / Model) this product fits. Uses SEMA / ACA VCdb data.
            </Typography>

            {semaUnavailable && (
                <Typography variant="body2" style={{color: '#c00', marginBottom: 12}}>
                    SEMA vehicle lookups are unavailable (check the SEMA Data subscription credentials in the API config).
                    Existing fitments are still shown below.
                </Typography>
            )}

            <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12}}>
                <Autocomplete
                    options={years}
                    getOptionLabel={(o) => String(o)}
                    value={selectedYear}
                    onChange={(e, v) => setSelectedYear(v)}
                    style={{width: 130}}
                    disabled={semaUnavailable || years.length === 0}
                    renderInput={(params) => <TextField {...params} label="Year" variant="outlined" size="small"/>}
                />
                <Autocomplete
                    options={makes}
                    getOptionLabel={(o) => o}
                    value={selectedMake}
                    onChange={(e, v) => setSelectedMake(v)}
                    style={{width: 220}}
                    disabled={!selectedYear || makes.length === 0}
                    renderInput={(params) => <TextField {...params} label="Make" variant="outlined" size="small"/>}
                />
                <Autocomplete
                    options={models}
                    getOptionLabel={(o) => o.modelName}
                    getOptionSelected={(o, v) => o.baseVehicleId === v.baseVehicleId}
                    value={selectedModel}
                    onChange={(e, v) => setSelectedModel(v)}
                    style={{width: 240}}
                    loading={loadingModels}
                    disabled={!selectedMake || models.length === 0}
                    renderInput={(params) => <TextField {...params} label="Model" variant="outlined" size="small"/>}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdd}
                    disabled={!selectedModel || saving}
                >
                    {saving ? 'Adding…' : 'Add vehicle'}
                </Button>
            </div>

            {error && <Typography variant="body2" style={{color: '#c00', marginBottom: 8}}>{error}</Typography>}

            {loadingFitments ? (
                <CircularProgress size={20}/>
            ) : fitments.length === 0 ? (
                <Typography variant="body2" style={{color: '#888'}}>No vehicles assigned yet.</Typography>
            ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                    {fitments.map((f) => (
                        <Chip
                            key={f.id}
                            label={`${f.year} ${f.makeName} ${f.modelName}`}
                            onDelete={() => handleRemove(f.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default VehicleFitmentPanel;
